import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import useTableDensity from '@/hooks/useTableDensity';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { LoginRecordDtoIn } from '@/models/in/LoginRecordDtoIn';
import filterStyles from '@/components/filters/FiltersArea.module.scss';
import { formatUserDate, userDateSortValue } from './userHelpers';

/** How many of the most recent login attempts the screen pulls - the same window the old site showed. */
const RECORDS_COUNT = 100;

const LOGINS_URL = `users/logins?count=${RECORDS_COUNT}`;
const DENSITY_STORAGE_KEY = 'table.density.loginAudit';

const SUCCESS_STATUS = 'Success';

type LoginStatus = 'Success' | 'Failure';

const LoginAuditPage = () => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [selectedStatuses, setSelectedStatuses] = useState<LoginStatus[]>([]);

  const { data: logins, isLoading } = useGetAuthApi<LoginRecordDtoIn[]>(LOGINS_URL);

  // The failure reasons and the password hints arrive as the server side enum names, each with its own label.
  const translateReason = useCallback((reason?: string) => (reason ? t(`loginAudit.failureReasons.${reason}`) : ''), [t]);
  const translateHint = useCallback((hint: string) => t(`loginAudit.passwordHints.${hint}`), [t]);

  const statusCounts = useMemo(() => {
    const counts: Record<LoginStatus, number> = { Success: 0, Failure: 0 };
    (logins ?? []).forEach((login) => {
      if (login.status === SUCCESS_STATUS) counts.Success += 1;
      else counts.Failure += 1;
    });
    return counts;
  }, [logins]);

  /** Free text search over everything the row shows - date, email, status, failure reason and hints. */
  const matchesSearch = useCallback(
    (login: LoginRecordDtoIn) => {
      const trimmed = searchQuery.trim().toLowerCase();
      if (!trimmed) return true;

      const haystack = [
        formatUserDate(login.timestamp),
        login.email,
        t(`loginAudit.statuses.${login.status}`),
        translateReason(login.failureReason),
        (login.passwordHints ?? []).map(translateHint).join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      // Every word has to appear somewhere, so "כהן סיסמה" finds Cohen's wrong password attempts.
      return trimmed.split(/\s+/).every((word) => haystack.includes(word));
    },
    [searchQuery, t, translateReason, translateHint]
  );

  const filteredLogins = useMemo(() => {
    return (logins ?? []).filter((login) => {
      const status: LoginStatus = login.status === SUCCESS_STATUS ? 'Success' : 'Failure';
      const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(status);

      return matchesStatus && matchesSearch(login);
    });
  }, [logins, selectedStatuses, matchesSearch]);

  const hasActiveFilters = selectedStatuses.length > 0 || searchQuery.trim() !== '';

  const toggleStatus = (status: LoginStatus) => {
    setSelectedStatuses((current) => (current.includes(status) ? current.filter((s) => s !== status) : [...current, status]));
  };

  const resetFilters = () => {
    setSelectedStatuses([]);
    setSearchQuery('');
  };

  const columns: ColumnDef<LoginRecordDtoIn>[] = [
    {
      id: 'timestamp',
      header: Col(t('loginAudit.columns.date')),
      enableSorting: true,
      accessorFn: (row) => userDateSortValue(row.timestamp),
      cell: ({ row }) => <span className="whitespace-nowrap">{formatUserDate(row.original.timestamp)}</span>,
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'email',
      header: Col(t('loginAudit.columns.email')),
      accessorKey: 'email',
      enableSorting: true,
      cell: ({ row }) => (
        <span dir="ltr" className="block text-start">
          {row.original.email}
        </span>
      ),
      // The address is laid out LTR, so it hugs the column's far edge - right against the status
      // next to it. The inline padding pushes it back and beats the shared .td shorthand.
      meta: { className: 'min-w-[260px]', style: { paddingInlineEnd: '44px' } },
    },
    {
      id: 'status',
      header: Col(t('loginAudit.columns.status')),
      accessorKey: 'status',
      enableSorting: true,
      cell: ({ row }) => {
        const isSuccess = row.original.status === SUCCESS_STATUS;
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <div className={`w-[10px] h-[10px] rounded-full ${isSuccess ? 'bg-green-500' : 'bg-red-500'}`} />
            <span>{t(`loginAudit.statuses.${row.original.status}`)}</span>
          </div>
        );
      },
      // ...and the status starts at its own column's near edge, so it moves the other way.
      meta: { className: 'min-w-[150px]', style: { paddingInlineStart: '44px' } },
    },
    {
      id: 'failureReason',
      header: Col(t('loginAudit.columns.error')),
      enableSorting: true,
      accessorFn: (row) => translateReason(row.failureReason),
      cell: ({ row }) => {
        const reason = translateReason(row.original.failureReason);
        return reason ? <span>{reason}</span> : <span className="text-(--font-body-3)">-</span>;
      },
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'passwordHints',
      header: Col(t('loginAudit.columns.passwordContains')),
      enableSorting: false,
      cell: ({ row }) => {
        const hints = row.original.passwordHints ?? [];
        if (hints.length === 0) return <span className="text-(--font-body-3)">-</span>;

        return (
          <div className="flex flex-wrap gap-1.5">
            {hints.map((hint) => (
              <span key={hint} className="px-2 py-0.5 rounded-full bg-(--surface-grey-low) text-(--font-body-1) body-xs-sm-regular whitespace-nowrap">
                {translateHint(hint)}
              </span>
            ))}
          </div>
        );
      },
      meta: { className: 'min-w-[240px]', hiddenOnMobile: true },
    },
  ];

  return (
    <div className="w-full">
      <DataTable
        title={t('loginAudit.title')}
        data={filteredLogins}
        isLoading={isLoading}
        columns={columns}
        sortBy={{ id: 'timestamp', desc: true }}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="loginAudit"
        searchPlaceholder="loginAudit.search"
        searchTooltip="loginAudit.searchTooltip"
        filters={
          <div className={filterStyles.filtersArea}>
            <div className={filterStyles.filtersContainer}>
              <StatusFilter
                title={t('loginAudit.statuses.Success')}
                isSelected={selectedStatuses.includes('Success')}
                onClick={() => toggleStatus('Success')}
                count={statusCounts.Success}
              />
              <StatusFilter
                title={t('loginAudit.statuses.Failure')}
                isSelected={selectedStatuses.includes('Failure')}
                onClick={() => toggleStatus('Failure')}
                count={statusCounts.Failure}
              />
              {hasActiveFilters && (
                <button className="body-sm-link text-(--font-link)" onClick={resetFilters}>
                  {t('resetFilters')}
                </button>
              )}
            </div>
          </div>
        }
        emptyStateContent={{
          title: t('loginAudit.emptyTitle'),
          description: t('loginAudit.emptyDescription'),
        }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={() => true}
      />
    </div>
  );
};

export default LoginAuditPage;
